import React, { useState } from 'react'
import { GitCompare } from 'lucide-react'
import { AnalyticsLayout } from '@/components/analytics/AnalyticsLayout'
import { ScorecardTable, ScorecardRow } from '@/components/analytics/ScorecardTable'
import { ScorecardCompareModal } from '@/components/analytics/ScorecardCompareModal'
import { ExportButton } from '@/components/shared/ExportButton'
import { ChartInfoButton } from '@/components/shared/ChartInfoButton'
import { useAnalyticsQuery } from '@/hooks/useAnalyticsQuery'
import { useAnalyticsFilters } from '@/contexts/AnalyticsFiltersContext'

interface ScorecardResponse {
  kind: 'developer'
  minProjects: number
  rows: ScorecardRow[]
}

function DeveloperBody() {
  const { queryParams } = useAnalyticsFilters()
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [compareOpen, setCompareOpen] = useState(false)
  const { data, isLoading, error } = useAnalyticsQuery<ScorecardResponse>(
    'scorecards.developers',
    '/analytics/scorecards/developers',
  )

  const canCompare = selectedIds.length >= 2 && selectedIds.length <= 4

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-2">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900">Developer scorecard</h2>
            <p className="text-xs text-secondary-500">
              Ranked by GMV. Shows sell-through %, median €/m² with percentile rank, pipeline value,
              and 90-day sales. Select 2–4 developers to compare trends side-by-side.
            </p>
          </div>
          <ChartInfoButton
            title="Developer scorecard"
            info={{
              definition:
                'Per-developer performance table ranked by GMV. Lets you benchmark absorption, pricing, and pipeline scale across developers in one view.',
              formula:
                'GMV = SUM(current_price of sold units) · sell-through = sold / (sold + available + reserved) × 100 · median €/m² percentile = rank of the developer\'s median €/m² against the universe · pipeline value = SUM(current_price of available units) · sales 90d = COUNT(unit) WHERE sales_date > now() - 90d',
              calculation:
                'Honors the global filter bar. Developers with fewer than 2 projects are hidden. Compare mode pulls the same entities\' weekly time series (sales, cumulative sell-through, avg €/m²) — select 2–4 rows via the checkbox column to enable the Compare button.',
              source: 'unit, project, developer',
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCompareOpen(true)}
            disabled={!canCompare}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-secondary-300 bg-white text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={
              canCompare
                ? `Compare ${selectedIds.length} selected developers`
                : 'Select 2–4 developers to enable compare mode'
            }
          >
            <GitCompare className="h-4 w-4" />
            Compare ({selectedIds.length})
          </button>
          <ExportButton
            endpoint="/analytics/export/scorecards/developers.csv"
            params={queryParams}
            filename="analytics-scorecards-developers"
          />
        </div>
      </div>
      <ScorecardTable
        kind="developer"
        rows={data?.rows}
        loading={isLoading}
        error={error}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />
      {compareOpen && canCompare && (
        <ScorecardCompareModal
          kind="developer"
          ids={selectedIds}
          onClose={() => setCompareOpen(false)}
        />
      )}
    </div>
  )
}

export function DeveloperScorecards() {
  return (
    <AnalyticsLayout filterVariant="developer">
      <DeveloperBody />
    </AnalyticsLayout>
  )
}
